// ---------------------------------------------------------------------------
// Nieve2 snow model — "Routing System II" (Schleiss, A., 2007, "Flow
// modelling in hydraulic systems", Communication 32, Laboratory of Hydraulic
// Constructions, EPFL Lausanne; MINERVE project, flood forecasting in
// alpine basins of Valais, Switzerland).
//
// Formulation confirmed against EvalHid's real calculation engine source
// (Nieve2.cs, class Nieve2.calculo_nieve2() — 2026-08, see CLAUDE.md's
// engine-source audit; supersedes an earlier verification against a
// decompiled Evalhid.dll, which had drifted from this on 3 real points):
//
// 1. The water release criterion (Step 3) uses θ RECOMPUTED WITHIN THE SAME
//    step (`Oo = Waux/Haux`, from this step's own intermediate water/snow
//    after melt/freeze), not θ from the previous step. That recomputed θ
//    decides both whether water releases at all and how much.
// 2. Release is also capped to the water actually available
//    (`Pliqt = Math.min(Waux, (Oo-Ocr)*Haux)`) — without the cap, the
//    formula alone could ask to release more than `Waux` holds.
// 3. Melt is capped to the snow ALREADY accumulated at the start of the
//    step (`Ho`), not `Ho` plus this same step's new solid precipitation.
//
// In the "all snow melted" case (HN<0.001, not exactly 0), the water
// released is P* + WN + (melt/freeze of this same step) — the intermediate
// Waux value just before the release logic, not last step's WN alone.
//
// The division that updates θ (Oo=Waux/Haux) is guarded against Haux=0
// (`if(Haux>0.001) Oo=Waux/Haux; else Oo=0.0;`) directly in the real
// source — an earlier decompiled build appeared to be missing this guard
// (producing Infinity/NaN), which this port had worked around as a
// deliberate safety deviation; the guard is real, so this is no longer a
// deviation, just a faithful port (kept as a >0.001 threshold, not a
// stricter >0, to match the real source's own threshold exactly).
// ---------------------------------------------------------------------------

export interface Snow2Parameters {
  /** An (mm/°C·step): degree-day coefficient. The only parameter meant to be calibrated; the rest are "recommended" by the manual. */
  an: number;
  /** Bp (1/mm): melt increase coefficient due to precipitation. */
  bp: number;
  /** Ocr (dimensionless, θcr): critical relative water content in the snow stock above which liquid water is released. */
  ocr: number;
  /** Tcp1 (°C): minimum critical temperature for liquid precipitation to occur. */
  tcp1: number;
  /** Tcp2 (°C): maximum critical temperature for solid precipitation to occur. */
  tcp2: number;
  /** Tcr (°C): critical temperature for snow melt onset. */
  tcr: number;
}

interface Snow2State {
  /** Ho (mm, initial HN): snow content in solid state. */
  ho: number;
  /** Oo (dimensionless, initial θ): proportion of liquid water in the snow stock. */
  oo: number;
}

interface Snow2Detail {
  [key: string]: Float64Array;
  /** HN (mm): snow content after the step. */
  snow: Float64Array;
  /** θ (dimensionless): proportion of liquid water in the snow stock after the step. */
  theta: Float64Array;
  /** MN (mm): melt (positive) or freeze (negative) for the step. */
  melt: Float64Array;
}

interface Snow2Result {
  /** Equivalent precipitation (Peq) per time step, mm — the input the rainfall-runoff model receives. */
  liquidPrecipitation: Float64Array;
  /** Step-by-step internal detail series (see Sal_parciales.csv in the real .exe). */
  detail: Snow2Detail;
}

/**
 * Simulates the Nieve2 snow model for a single sub-basin.
 */
export function calculateSnow2(
  precipitation: Float64Array,
  temperature: Float64Array,
  parameters: Snow2Parameters,
  initialState: Snow2State
): Snow2Result {
  const { an, bp, ocr, tcp1, tcp2, tcr } = parameters;
  const n = precipitation.length;
  const liquidPrecipitation = new Float64Array(n);
  const detail: Snow2Detail = {
    snow: new Float64Array(n),
    theta: new Float64Array(n),
    melt: new Float64Array(n),
  };

  let hn = initialState.ho;
  let oo = initialState.oo;
  let wn = oo * hn;

  for (let t = 0; t < n; t++) {
    const p = Number.isFinite(precipitation[t]) ? precipitation[t] : 0;
    // With no temperature data, it's treated as "below both thresholds" (solid precipitation, no melt).
    const temp = Number.isFinite(temperature[t]) ? temperature[t] : Math.min(tcp1, tcr);

    // Step 1: rain/snow partition according to the Tcp1-Tcp2 transition zone.
    let rainFraction: number;
    if (temp < tcp1) rainFraction = 0;
    else if (temp > tcp2) rainFraction = 1;
    else rainFraction = (temp - tcp1) / (tcp2 - tcp1);
    const liquidP = rainFraction * p;
    const solidP = (1 - rainFraction) * p;

    // Step 2: melt (fus≥0) or freeze (cong≥0), kept as two separate
    // non-negative quantities (matching the real source exactly) rather
    // than one signed value — melt is capped to the snow already
    // accumulated BEFORE this step (hn), not hn+solidP.
    let fus = 0;
    let cong = 0;
    if (temp > tcr) {
      fus = Math.min(hn, an * (1 + bp * liquidP) * (temp - tcr));
    } else {
      cong = Math.min(wn, an * (tcr - temp));
    }

    const intermediateHn = hn + solidP - fus + cong;
    const intermediateWn = wn + liquidP + fus - cong;

    // Step 3: water release. θ is recomputed WITHIN this same step (not
    // carried over from the previous one) and used immediately to decide
    // both whether water releases and how much, capped to what's available.
    let peq: number;
    let nextHn: number;
    let nextWn: number;
    if (intermediateHn < 0.001) {
      peq = intermediateWn;
      nextHn = 0;
      nextWn = 0;
    } else {
      const stepTheta = intermediateWn / intermediateHn;
      if (stepTheta < ocr) {
        peq = 0;
        nextWn = intermediateWn;
      } else {
        peq = Math.min(intermediateWn, (stepTheta - ocr) * intermediateHn);
        nextWn = intermediateWn - peq;
      }
      nextHn = intermediateHn;
    }

    liquidPrecipitation[t] = peq;

    hn = nextHn;
    wn = nextWn;
    oo = hn > 0.001 ? wn / hn : 0;

    detail.snow[t] = hn;
    detail.theta[t] = oo;
    detail.melt[t] = fus - cong;
  }

  return { liquidPrecipitation, detail };
}
